import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react'

// 支持的语言类型
export type Language = 'en' | 'zh'

// 语言上下文类型
interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

// 翻译字典
const translations = {
  en: {
    // 导航栏
    'nav.home': 'Home',
    'nav.industries': 'Industries',
    'nav.countries': 'Countries',
    'nav.news': 'AI News',
    'nav.about': 'About',
    'nav.tools': '🚀 AI Tools',
    'nav.test': '🧪 Data Test',
    
    // 首页
    'home.title': '🌍 AI Global Penetration Monitoring Center',
    'home.subtitle': 'Real-time monitoring of AI penetration across global industries + Curated AI tool navigation + Community interactive discussions',
    'home.currentRate': 'Current Global AI Penetration Rate',
    'home.companies': 'Companies Using AI',
    'home.investment': 'AI Investment',
    'home.projects': 'Active Projects',
    'home.trends': 'AI Adoption Trends',
    'home.capabilities': 'AI Capabilities Assessment',
    'home.scenarios': 'Future AI Scenarios',
    'home.mainTitle': '🌍 AI Global Penetration Monitoring Center',
    'home.mainSubtitle': 'Real-time monitoring of AI penetration across global industries + Curated AI tool navigation + Community interactive discussions',
    'home.dataMonitoring': '📊 Data Monitoring',
    'home.toolNavigation': '🚀 AI Tool Navigation',
    'home.communityDiscussion': '💭 Community Discussion',
    'home.futurePrediction': '🔮 Future Prediction',
    'home.industryRankingTitle': 'Industry AI Penetration Rankings',
    'home.globalPenetrationTitle': 'Global AI Penetration',
    'home.comprehensiveCalculation': 'Comprehensive calculation based on multi-dimensional data',
    'home.lastUpdated': 'Last updated',
    'home.loading': 'Loading...',
    'home.past24Hours': 'Past 24 hours',
    
    // 圆形进度图表
    'chart.calculating': 'Calculating...',
    'chart.aiGlobalPenetration': 'AI Global Penetration',
    'chart.comprehensiveCalculation': 'Comprehensive calculation based on global data',
    'chart.lastUpdated': 'Last updated',
    
    // 趋势图表
    'trend.title': 'AI Penetration Historical Trends',
    'trend.subtitle': 'Trajectory of global AI adoption rate changes over the past five years',
    'trend.fiveYearGrowth': 'Five-Year Growth',
    'trend.predictedExceed': 'Predicted to exceed 50%',
    'trend.aiPenetration': 'AI Penetration (%)',
    'trend.year': 'Year',
    
    // AI时间线
    'timeline.title': 'AI Evolution Timeline',
    'timeline.subtitle': 'Interactive journey of AI development and global adoption',
    'timeline.futureProjection': 'Future Projection',
    'timeline.aiPenetration': 'AI Penetration',
    'timeline.gpt2Release': 'GPT-2 Release',
    'timeline.gpt2Description': 'OpenAI releases GPT-2, marking the beginning of large language model era',
    'timeline.covidAcceleration': 'COVID-19 AI Acceleration',
    'timeline.covidDescription': 'Pandemic drives rapid adoption of AI in healthcare and remote work',
    'timeline.enterpriseBoom': 'Enterprise AI Boom',
    'timeline.enterpriseDescription': 'Major corporations begin large-scale AI integration across industries',
    'timeline.chatgptLaunch': 'ChatGPT Launch',
    'timeline.chatgptDescription': 'ChatGPT reaches 100M users in 2 months, democratizing AI access',
    'timeline.investmentPeak': 'Global AI Investment Peak',
    'timeline.investmentDescription': 'AI investments reach $93.6B globally, with major regulatory frameworks emerging',
    'timeline.currentState': 'Current State',
    'timeline.currentDescription': 'AI penetration reaches current levels with widespread enterprise adoption',
    'timeline.breakthrough': 'breakthrough',
    'timeline.adoption': 'adoption',
    'timeline.investment': 'investment',
    
    // AI能力评估
    'capabilities.title': 'AI Capabilities Assessment',
    'capabilities.subtitle': 'Current AI performance across key capability domains',
    'capabilities.coding': 'Coding',
    'capabilities.hacking': 'Hacking',
    'capabilities.research': 'Research',
    'capabilities.automation': 'Automation',
    'capabilities.socialIntelligence': 'Social Intelligence',
    'capabilities.globalImpact': 'Global Impact',
    'capabilities.ofHumanLevel': 'of human-level',
    'capabilities.riskLegend': 'Risk Assessment Legend',
    'capabilities.low': 'Low',
    'capabilities.medium': 'Medium',
    'capabilities.high': 'High',
    'capabilities.critical': 'Critical',
    
    // AI场景
    'scenarios.title': 'Choose Your AI Future',
    'scenarios.subtitle': 'Explore different scenarios for AI development over the next decade',
    'scenarios.acceleration': 'AI Acceleration',
    'scenarios.accelerationDesc': 'Rapid AI advancement with minimal regulatory constraints, leading to exponential growth in AI capabilities and adoption.',
    'scenarios.controlled': 'Controlled Progress',
    'scenarios.controlledDesc': 'Balanced AI development with effective governance frameworks, ensuring safe and beneficial AI deployment.',
    'scenarios.slowdown': 'AI Slowdown',
    'scenarios.slowdownDesc': 'Significant regulatory restrictions or technical barriers slow AI progress, limiting widespread adoption.',
    'scenarios.probability': 'probability',
    'scenarios.penetration2030': '2030 Penetration',
    'scenarios.projectedPenetration': 'Projected AI Penetration',
    'scenarios.current': 'Current',
    'scenarios.projected': 'Projected',
    'scenarios.probabilityIndicator': 'Probability',
    
    // 行业页面
    'industries.title': 'AI Penetration by Industry',
    'industries.subtitle': 'Comprehensive analysis of artificial intelligence adoption across different sectors',
    'industries.pageTitle': 'Industry AI Penetration Analysis',
    'industries.pageSubtitle': 'Deep insights into AI adoption and development trends across major industries',
    'industries.financial': 'Financial Technology',
    'industries.financialDesc': 'AI widely applied in risk assessment, fraud detection and algorithmic trading',
    'industries.healthcare': 'Healthcare',
    'industries.healthcareDesc': 'Rapid growth in AI applications for medical imaging, drug discovery and personalized treatment',
    'industries.manufacturing': 'Manufacturing',
    'industries.manufacturingDesc': 'AI solutions for smart manufacturing, predictive maintenance and quality control',
    'industries.retail': 'Retail & E-commerce',
    'industries.retailDesc': 'Recommendation systems, demand forecasting and customer service automation',
    'industries.technology': 'Technology',
    'industries.energy': 'Energy',
    'industries.transportation': 'Transportation',
    'industries.aiPenetration': 'AI Penetration',
    'industries.viewDetails': 'View Details',
    // 行业名称（根据dataConfig.ts）
    'industries.infoTech': 'Information Technology & Software',
    'industries.media': 'Media & Content Creation',
    'industries.financeServices': 'Financial Services',
    'industries.healthcareLife': 'Healthcare & Life Sciences',
    'industries.manufacturingIndustrial': 'Manufacturing & Industrial',
    'industries.retailEcommerce': 'Retail & E-commerce',
    'industries.transportationLogistics': 'Transportation & Logistics',
    'industries.professionalServices': 'Professional Services',
    'industries.education': 'Education',
    'industries.scientificResearch': 'Scientific Research',
    'industries.energyUtilities': 'Energy & Utilities',
    'industries.publicSector': 'Government & Public Sector',
    
    // 国家页面
    'countries.title': 'Global AI Leadership Rankings',
    'countries.subtitle': 'Countries leading in artificial intelligence development and adoption',
    'countries.pageTitle': 'Global AI Leadership Rankings',
    'countries.pageSubtitle': 'Countries at the forefront of artificial intelligence development and adoption',
    'countries.rank': 'Rank',
    'countries.country': 'Country',
    'countries.penetration': 'AI Penetration',
    'countries.companies': 'AI Companies',
    'countries.investment': 'Investment',
    'countries.countriesCovered': 'Countries Covered',
    
    // 新闻页面
    'news.title': 'Latest AI News & Developments',
    'news.subtitle': 'Stay updated with the latest breakthroughs in artificial intelligence',
    'news.category.technology': 'Technology',
    'news.category.investment': 'Investment',
    'news.category.regulation': 'Regulation',
    'news.category.healthcare': 'Healthcare',
    'news.category.autonomous': 'Autonomous Driving',
    'news.latestAI': 'Latest AI News',
    'news.viewMore': 'View More',
    'news.realTime': 'Real-time',
    'news.featured': 'Featured',
    'news.item1.title': 'OpenAI Releases GPT-4.5, AI Reasoning Capabilities Break Through Again',
    'news.item1.source': 'TechCrunch',
    'news.item1.time': '2 hours ago',
    'news.item2.title': 'EU AI Act Officially Takes Effect, Setting New Global AI Regulatory Benchmarks',
    'news.item2.source': 'Reuters',
    'news.item2.time': '5 hours ago',
    'news.item3.title': 'Study Shows: AI Accuracy in Early Cancer Screening Surpasses Human Experts',
    'news.item3.source': 'Nature',
    'news.item3.time': '8 hours ago',
    'news.item4.title': 'Google Invests $2 Billion in AI Startup Anthropic',
    'news.item4.source': 'Bloomberg',
    'news.item4.time': '1 day ago',
    'news.noNewsFound': 'No news found',
    'news.checkAPIKey': 'Please check your NewsAPI key or try again later.',
    
    // 关于页面
    'about.title': 'About AIWorldMeter',
    'about.mission': 'Our Mission',
    'about.team': 'Our Team',
    'about.features': 'Core Features',
    
    // 通用
    'common.readMore': 'Read More',
    'common.viewAll': 'View All',
    'common.loading': 'Loading...',
    'common.error': 'Error loading data',
    'common.exploreGlobalImpact': 'Explore AI\'s Global Impact',
    'common.discoverDetailed': 'Discover detailed data across industries and countries, uncovering new trends in AI development',
    
    // Time units
    'time.yearsAgo': 'years ago',
    'time.monthsAgo': 'months ago',
    'time.daysAgo': 'days ago',
    'time.hoursAgo': 'hours ago',
    'time.minutesAgo': 'minutes ago',
    'time.secondsAgo': 'seconds ago',

    // AI Future Interaction
    'future.title': 'Join the Great AI Discussion',
    'future.addComment': 'Join Discussion',
    'future.user1.name': 'AI Frontier Observer',
    'future.user1.role': 'Developer',
    'future.user1.comment': 'I believe AI will first be universally applied in repetitive, high-volume mental labor, such as data analysis, basic coding, and contract review. This will greatly increase productivity.',
    'future.user2.name': 'Sci-Fi Author',
    'future.user2.role': 'Creative Worker',
    'future.user2.comment': 'The application of AGI is a new industrial revolution. We may need to redefine the meaning of "work". The key lies in whether we can establish effective regulatory and ethical frameworks before that.',
    'future.modal.title': 'Share Your Opinion',
    'future.modal.placeholder': 'Share your unique insights on the future of AI...',
    'future.modal.submit': 'Post Comment',
    'future.modal.errorShort': 'Comment must be at least 5 characters long.',

    // AI Tools Navigation
    'tools.title': 'Curated AI Tools Navigation',
    'tools.subtitle': 'We have carefully selected the most cutting-edge and practical AI tools in various fields to help you start the intelligent era.',
    'tools.category.creative': '🎨 AI Creative Tools',
    'tools.category.business': '💼 Business AI Applications',
    'tools.category.developer': '🔬 Developer Tools',
    'tools.category.learning': '🎓 Learning Resources',
  },
  zh: {
    // 导航栏
    'nav.home': '首页',
    'nav.industries': '行业分析',
    'nav.countries': '国家排名',
    'nav.news': 'AI资讯',
    'nav.about': '关于我们',
    'nav.tools': '🚀 AI工具',
    'nav.test': '🧪 数据测试',
    
    // 首页
    'home.title': '🌍 实时AI全球渗透率监控',
    'home.subtitle': '追踪人工智能在全球各行业和国家的采用率',
    'home.currentRate': '当前全球AI渗透率',
    'home.companies': '使用AI的企业',
    'home.investment': 'AI投资金额',
    'home.projects': '活跃项目',
    'home.trends': 'AI采用趋势',
    'home.capabilities': 'AI能力评估',
    'home.scenarios': '未来AI场景',
    'home.mainTitle': '🌍 AI全球渗透率监控中心',
    'home.mainSubtitle': '实时监控AI在全球各行业的渗透率 + 精选AI工具导航 + 社区互动讨论',
    'home.dataMonitoring': '📊 数据监控',
    'home.toolNavigation': '🚀 AI工具导航',
    'home.communityDiscussion': '💭 社区讨论',
    'home.futurePrediction': '🔮 未来预测',
    'home.industryRankingTitle': '各行业AI渗透率排名',
    'home.globalPenetrationTitle': '全球AI渗透率',
    'home.comprehensiveCalculation': '基于多维度数据的综合计算',
    'home.lastUpdated': '最后更新',
    'home.loading': '加载中...',
    'home.past24Hours': '过去24小时',
    
    // 圆形进度图表
    'chart.calculating': '计算中...',
    'chart.aiGlobalPenetration': '全球AI渗透率',
    'chart.comprehensiveCalculation': '基于全球数据的综合计算',
    'chart.lastUpdated': '最后更新',
    
    // 趋势图表
    'trend.title': 'AI渗透率历史趋势',
    'trend.subtitle': '过去五年全球AI采用率变化轨迹',
    'trend.fiveYearGrowth': '五年增长',
    'trend.predictedExceed': '预计将超过50%',
    'trend.aiPenetration': 'AI渗透率 (%)',
    'trend.year': '年份',
    
    // AI时间线
    'timeline.title': 'AI发展时间线',
    'timeline.subtitle': 'AI发展和全球采用的互动历程',
    'timeline.futureProjection': '未来预测',
    'timeline.aiPenetration': 'AI渗透率',
    'timeline.gpt2Release': 'GPT-2发布',
    'timeline.gpt2Description': 'OpenAI发布GPT-2，标志着大型语言模型时代的开始',
    'timeline.covidAcceleration': 'COVID-19 AI加速',
    'timeline.covidDescription': '疫情推动AI在医疗保健和远程工作中的快速采用',
    'timeline.enterpriseBoom': '企业AI繁荣',
    'timeline.enterpriseDescription': '各大企业开始跨行业大规模整合AI',
    'timeline.chatgptLaunch': 'ChatGPT上线',
    'timeline.chatgptDescription': 'ChatGPT在2个月内达到1亿用户，普及了AI使用',
    'timeline.investmentPeak': '全球AI投资高峰',
    'timeline.investmentDescription': '全球AI投资达到936亿美元，主要监管框架开始出现',
    'timeline.currentState': '当前状态',
    'timeline.currentDescription': 'AI渗透率达到目前水平，企业广泛采用',
    'timeline.breakthrough': '突破',
    'timeline.adoption': '采用',
    'timeline.investment': '投资',
    
    // AI能力评估
    'capabilities.title': 'AI能力评估',
    'capabilities.subtitle': '当前AI在关键能力领域的表现',
    'capabilities.coding': '编程',
    'capabilities.hacking': '黑客攻击',
    'capabilities.research': '研究',
    'capabilities.automation': '自动化',
    'capabilities.socialIntelligence': '社交智能',
    'capabilities.globalImpact': '全球影响',
    'capabilities.ofHumanLevel': '人类水平',
    'capabilities.riskLegend': '风险评估图例',
    'capabilities.low': '低',
    'capabilities.medium': '中',
    'capabilities.high': '高',
    'capabilities.critical': '关键',
    
    // AI场景
    'scenarios.title': '选择您的AI未来',
    'scenarios.subtitle': '探索未来十年AI发展的不同场景',
    'scenarios.acceleration': 'AI加速',
    'scenarios.accelerationDesc': '在最少监管约束下AI快速发展，导致AI能力和采用率指数级增长。',
    'scenarios.controlled': '受控进展',
    'scenarios.controlledDesc': '平衡的AI发展与有效的治理框架，确保安全和有益的AI部署。',
    'scenarios.slowdown': 'AI放缓',
    'scenarios.slowdownDesc': '重大监管限制或技术障碍减缓AI进步，限制广泛采用。',
    'scenarios.probability': '概率',
    'scenarios.penetration2030': '2030年渗透率',
    'scenarios.projectedPenetration': '预测AI渗透率',
    'scenarios.current': '当前',
    'scenarios.projected': '预测',
    'scenarios.probabilityIndicator': '概率',
    
    // 行业页面
    'industries.title': '各行业AI渗透率',
    'industries.subtitle': '全面分析人工智能在不同行业的采用情况',
    'industries.pageTitle': '行业AI渗透率分析',
    'industries.pageSubtitle': '深入了解各主要行业的AI采用和发展趋势',
    'industries.financial': '金融科技',
    'industries.financialDesc': 'AI在风险评估、欺诈检测和算法交易中广泛应用',
    'industries.healthcare': '医疗保健',
    'industries.healthcareDesc': 'AI在医学影像、药物发现和个性化治疗应用中快速增长',
    'industries.manufacturing': '制造业',
    'industries.manufacturingDesc': '智能制造、预测维护和质量控制的AI解决方案',
    'industries.retail': '零售电商',
    'industries.retailDesc': '推荐系统、需求预测和客户服务自动化',
    'industries.technology': '科技行业',
    'industries.energy': '能源行业',
    'industries.transportation': '交通运输',
    'industries.aiPenetration': 'AI渗透率',
    'industries.viewDetails': '查看详情',
    // 行业名称（根据dataConfig.ts）
    'industries.infoTech': '信息技术与软件',
    'industries.media': '媒体与内容创作',
    'industries.financeServices': '金融服务',
    'industries.healthcareLife': '医疗健康与生命科学',
    'industries.manufacturingIndustrial': '制造与工业',
    'industries.retailEcommerce': '零售与电子商务',
    'industries.transportationLogistics': '交通与物流',
    'industries.professionalServices': '专业服务',
    'industries.education': '教育',
    'industries.scientificResearch': '科学研究',
    'industries.energyUtilities': '能源与公共事业',
    'industries.publicSector': '政府与公共部门',
    
    // 国家页面
    'countries.title': '全球AI领导力排名',
    'countries.subtitle': '在人工智能发展和应用方面领先的国家',
    'countries.pageTitle': '全球AI领导力排名',
    'countries.pageSubtitle': '在人工智能发展和应用前沿的国家',
    'countries.rank': '排名',
    'countries.country': '国家',
    'countries.penetration': 'AI渗透率',
    'countries.companies': 'AI企业',
    'countries.investment': '投资金额',
    'countries.countriesCovered': '覆盖国家',
    
    // 新闻页面
    'news.title': '最新AI资讯与发展',
    'news.subtitle': '了解人工智能领域的最新突破',
    'news.category.technology': '技术',
    'news.category.investment': '投资',
    'news.category.regulation': '监管',
    'news.category.healthcare': '医疗',
    'news.category.autonomous': '自动驾驶',
    'news.latestAI': '最新AI资讯',
    'news.viewMore': '查看更多资讯',
    'news.realTime': '实时',
    'news.featured': '精选',
    'news.item1.title': 'OpenAI发布GPT-4.5，AI推理能力再获突破',
    'news.item1.source': 'TechCrunch',
    'news.item1.time': '2小时前',
    'news.item2.title': '欧盟AI法案正式生效，为全球AI监管设立新标杆',
    'news.item2.source': 'Reuters',
    'news.item2.time': '5小时前',
    'news.item3.title': '研究显示：AI在早期癌症筛查中的准确率已超越人类专家',
    'news.item3.source': 'Nature',
    'news.item3.time': '8小时前',
    'news.item4.title': '谷歌投资20亿美元于AI初创公司Anthropic',
    'news.item4.source': 'Bloomberg',
    'news.item4.time': '1天前',
    'news.noNewsFound': '暂无相关新闻',
    'news.checkAPIKey': '请检查您的NewsAPI密钥或稍后再试。',
    
    // 关于页面
    'about.title': '关于AIWorldMeter',
    'about.mission': '我们的使命',
    'about.team': '我们的团队',
    'about.features': '核心功能',
    
    // 通用
    'common.readMore': '阅读更多',
    'common.viewAll': '查看全部',
    'common.loading': '加载中...',
    'common.error': '数据加载失败',
    'common.exploreGlobalImpact': '探索AI的全球影响',
    'common.discoverDetailed': '发现各行业和国家的详细数据，揭示AI发展的新趋势',

    // 时间单位
    'time.yearsAgo': '年前',
    'time.monthsAgo': '月前',
    'time.daysAgo': '天前',
    'time.hoursAgo': '小时前',
    'time.minutesAgo': '分钟前',
    'time.secondsAgo': '秒前',

    // AI 未来大讨论
    'future.title': 'AI未来大讨论',
    'future.addComment': '加入讨论',
    'future.user1.name': 'AI前沿观察家',
    'future.user1.role': '开发者',
    'future.user1.comment': '我认为AI将首先在重复性高的脑力劳动中普及，比如数据分析、初级编程、合同审查等。这会极大提升生产效率。',
    'future.user2.name': '科幻小说迷',
    'future.user2.role': '创意工作者',
    'future.user2.comment': '通用人工智能（AGI）的实现是一场全新的工业革命，我们很可能需要重新定义"工作"的意义。关键在于我们能否在此之前建立起有效的监管和伦理框架。',
    'future.modal.title': '发表您的看法',
    'future.modal.placeholder': '分享您对AI未来的独到见解...',
    'future.modal.submit': '发布评论',
    'future.modal.errorShort': '评论内容不能少于5个字符',

    // AI 工具导航
    'tools.title': '精选AI工具导航',
    'tools.subtitle': '我们为您精心挑选了各个领域最前沿、最实用的AI工具，助您开启智能时代。',
    'tools.category.creative': '🎨 AI创作工具',
    'tools.category.business': '💼 商业AI应用',
    'tools.category.developer': '🔬 开发工具',
    'tools.category.learning': '🎓 学习资源',
  }
}

// 创建上下文
const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// 语言提供者组件
export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('aiworldmeter-language') as Language
      console.log('🔍 初始化语言设置:', savedLanguage)
      return savedLanguage || 'en'
    }
    return 'en'
  })

  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem('aiworldmeter-language', language);
  }, [language]);

  const t = useMemo(() => {
    return (key: string): string => {
      const langDict = translations[language] || translations.en;
      const translation = langDict[key as keyof typeof langDict] || key;
      // 对于主要键进行调试
      if (key.includes('mainTitle') || key.includes('globalPenetration')) {
        console.log('🔤 翻译调试:', { key, language, translation })
      }
      return translation;
    };
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t
  }), [language, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

// 自定义Hook
export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
} 